import React, { useEffect, useState } from 'react';
import { Progress } from 'antd';
import { useNavigate } from 'react-router-dom';
import StatCard from './StatCard';
import { getDashboardApi, getUserMetricsApi, markActivityReadApi } from '../../services/api';

export default function Dashboard() {
  const [dashboard, setDashboard] = useState({});
  const [liveNow, setLiveNow] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboard();
  }, []);

  // "Live" is the whole point of this one card — everything else on this
  // page is a page-load snapshot, this is the deliberate exception.
  useEffect(() => {
    fetchLiveNow();
    const interval = setInterval(fetchLiveNow, 30_000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboard = async () => {
    try {
      const res = await getDashboardApi();
      setDashboard(res.data.data || res.data || {});
    } catch (err) {
      console.log(err);
    }
  };

  const fetchLiveNow = async () => {
    try {
      const res = await getUserMetricsApi();
      setLiveNow(res.data.data?.liveNow ?? 0);
    } catch {
      // Non-fatal — the card just shows the last known value.
    }
  };

  const stats = {
    totalRevenue:    dashboard.totalRevenue    || 0,
    ordersToday:     dashboard.ordersToday     || 0,
    activeCustomers: dashboard.activeCustomers || 0,
    lowStock:        dashboard.lowStockAlerts  || 0,
    deliveredToday:  dashboard.deliveredToday  || 0,
    pendingShipment: dashboard.pendingShipment || 0,
  };

  const topProducts = dashboard.topProducts || [];
  const activities = dashboard.activities || [];

  // Clicking an activity takes the admin to that order and marks it read for
  // THIS admin so it drops off Recent Activity — other admins still see it
  // until they've each opened it themselves.
  const handleActivityClick = (activity) => {
    if (!activity.orderId) return;
    markActivityReadApi(activity.orderId).catch(() => {
      // Non-fatal — worst case it still shows up on the next dashboard load.
    });
    navigate('/orders', { state: { openOrderId: activity.orderId } });
  };

  return (
    <div>

      {/* HEADER */}
      <div className="page-header">
        <div>
          <div className="page-title">Good morning, Admin 👋</div>
          <div className="page-sub">Here's what's happening today</div>
        </div>
      </div>

      {/* STATS */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 16,
        marginBottom: 24
      }}>
        <StatCard label="LIVE USERS NOW"    value={liveNow}                    positive color="#1E88E5" />
        <StatCard label="TOTAL REVENUE"     value={`₹${stats.totalRevenue}`}  positive />
        <StatCard label="ORDERS TODAY"      value={stats.ordersToday}          positive />
        <StatCard label="ACTIVE CUSTOMERS"  value={stats.activeCustomers}      positive />
        <StatCard label="LOW STOCK ALERTS"  value={stats.lowStock}             positive={false} color="#e24b4a" />
        <StatCard label="TOTAL DELIVERED"    value={stats.deliveredToday}       positive color="#43A047" />
        <StatCard label="READY TO SHIP"     value={stats.pendingShipment}      positive color="#1565C0" />
      </div>

      {/* GRID */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: window.innerWidth < 768 ? '1fr' : '1fr 320px',
        gap: 16
      }}>

        {/* PRODUCTS */}
        <div style={{
          background: '#fff',
          borderRadius: 12,
          border: '1px solid rgba(0,0,0,0.08)',
          padding: 20
        }}>
          <div className="section-title">Top Selling Products</div>

          {topProducts.length === 0 && (
            <div style={{ color: '#888' }}>No product data</div>
          )}

          {topProducts.map((p, i) => (
            <div key={i} style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '10px 0',
              borderBottom: '1px solid #eee'
            }}>
              <div>
                <div style={{ fontWeight: 500,marginBottom:5 }}>{p.name}</div>
                <div style={{ fontSize: 11, color: '#888' }}>{p.sku}</div>
              </div>

              <div style={{ textAlign: 'right' }}>
                <div>{p.sales} sold</div>
                <Progress
                  percent={Math.min(p.sales, 100)}
                  showInfo={false}
                  strokeColor="#534ab7"
                />
              </div>
            </div>
          ))}
        </div>

        {/* ACTIVITY */}
        <div style={{
          background: '#fff',
          borderRadius: 12,
          border: '1px solid rgba(0,0,0,0.08)',
          padding: 20
        }}>
          <div className="section-title">Recent Activity</div>

          {activities.length === 0 && (
            <div style={{ color: '#888' }}>No activity yet</div>
          )}

          {activities.map((a, i) => (
            <div
              key={i}
              onClick={() => handleActivityClick(a)}
              style={{ marginBottom: 10, cursor: a.orderId ? 'pointer' : 'default' }}
            >
              <div style={{ fontWeight: 500 }}>{a.text}</div>
              <div style={{ fontSize: 11, color: '#888' }}>{a.time}</div>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
}
